/**
 * Component: MinisterDataMergeSection
 * Diff between the data a minister submitted on the public form (snapshot) and
 * the master minister record; admin picks which changes to apply.
 * Public submissions never write to `ministers` directly.
 */

import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowRight, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/supabase.config';
import type { Database } from '@/types/supabase';

const MERGEABLE: { key: string; label: string }[] = [
  { key: 'full_name', label: 'Nombre' },
  { key: 'cell_phone', label: 'Celular' },
  { key: 'office_phone', label: 'Tel. oficina' },
  { key: 'email', label: 'E-mail' },
  { key: 'address', label: 'Dirección' },
  { key: 'position', label: 'Cargo' },
  { key: 'birth_date', label: 'Fecha de nacimiento' },
  { key: 'district_id', label: 'Distrito' },
  { key: 'region_id', label: 'Región' },
  { key: 'degree_id', label: 'Grado' },
];

const LOOKUP_KEYS = new Set(['district_id', 'region_id', 'degree_id']);

interface Props {
  ministerId: string;
  snapshot: Record<string, unknown>;
}

export function MinisterDataMergeSection({ ministerId, snapshot }: Props) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['minister_merge_source', ministerId],
    queryFn: async () => {
      const [minister, districts, regions, degrees] = await Promise.all([
        supabase.from('ministers').select(MERGEABLE.map((f) => f.key).join(', ')).eq('id', ministerId).single(),
        supabase.from('districts').select('id, name'),
        supabase.from('regions').select('id, name'),
        supabase.from('degrees').select('id, name'),
      ]);
      if (minister.error) throw new Error(minister.error.message);
      const names = new Map<string, string>();
      [...(districts.data ?? []), ...(regions.data ?? []), ...(degrees.data ?? [])].forEach((r) =>
        names.set(r.id, r.name as string),
      );
      return { current: minister.data as unknown as Record<string, unknown>, names };
    },
  });

  const diffs = useMemo(() => {
    if (!data) return [];
    return MERGEABLE.filter(({ key }) => {
      const next = snapshot[key];
      if (next === undefined || next === null || String(next).trim() === '') return false;
      return String(next).trim() !== String(data.current[key] ?? '').trim();
    });
  }, [data, snapshot]);

  if (isLoading) return null;
  if (!data || diffs.length === 0) {
    return <p className="text-sm text-muted-foreground">Los datos enviados coinciden con la ficha del ministro.</p>;
  }

  const display = (key: string, value: unknown) => {
    if (value === null || value === undefined || value === '') return '—';
    return LOOKUP_KEYS.has(key) ? data.names.get(String(value)) ?? String(value) : String(value);
  };

  const toggle = (key: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const apply = async () => {
    setSaving(true);
    try {
      const update = Object.fromEntries([...selected].map((k) => [k, String(snapshot[k]).trim()])) as Database['public']['Tables']['ministers']['Update'];
      const { error } = await supabase.from('ministers').update(update).eq('id', ministerId);
      if (error) throw new Error(error.message);
      toast({ title: 'Ficha del ministro actualizada' });
      setSelected(new Set());
      queryClient.invalidateQueries({ queryKey: ['minister_merge_source', ministerId] });
    } catch (err) {
      toast({ title: err instanceof Error ? err.message : 'No se pudo actualizar', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-2">
      <ul className="divide-y rounded-md border text-sm">
        {diffs.map(({ key, label }) => (
          <li key={key} className="flex items-center gap-3 px-3 py-2">
            <Checkbox checked={selected.has(key)} onCheckedChange={() => toggle(key)} aria-label={label} />
            <span className="w-28 shrink-0 text-muted-foreground">{label}</span>
            <span className="min-w-0 flex-1 truncate line-through decoration-muted-foreground/60">
              {display(key, data.current[key])}
            </span>
            <ArrowRight className="size-4 shrink-0 text-muted-foreground" />
            <span className="min-w-0 flex-1 truncate font-medium">{display(key, snapshot[key])}</span>
          </li>
        ))}
      </ul>
      <Button size="sm" variant="outline" onClick={apply} disabled={selected.size === 0 || saving}>
        {saving && <Loader2 className="mr-2 size-4 animate-spin" />}
        Actualizar ficha ({selected.size})
      </Button>
    </div>
  );
}
